import { useState, useEffect } from "react";
import { Music, Eye, Calendar, Mail, Menu, X, User, FileText, Disc3, Download } from "lucide-react";

export const Navigation = ({ onBookNow }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "About", href: "#about", icon: User },
    { label: "Music", href: "#music", icon: Music },
    { label: "Releases", href: "#releases", icon: Disc3 },
    { label: "Remixes", href: "#remixes", icon: Download },
    { label: "EPK", href: "#epk", icon: FileText },
    { label: "Events", href: "#events", icon: Calendar },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "glass py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <a
          href="/"
          data-testid="nav-logo"
          className="hover:opacity-80 transition-opacity duration-300"
        >
          <img src="/images/alchemist-logo.png" alt="Alchemist (CA)" className="h-8 md:h-10 w-auto" />
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6 lg:gap-10">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              data-testid={`nav-${link.label.toLowerCase()}`}
              onClick={(e) => handleNavClick(e, link.href)}
              className="font-sub text-[11px] text-white/40 hover:text-white tracking-widest transition-colors duration-300 relative group"
            >
              {link.label.toUpperCase()}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-white/60 group-hover:w-full transition-all duration-300" />
            </a>
          ))}
          <a
            href="mailto:contact@dj-alchemist.com"
            data-testid="nav-contact"
            className="font-sub text-[11px] text-white/40 hover:text-white tracking-widest transition-colors duration-300 relative group"
          >
            CONTACT
            <span className="absolute -bottom-1 left-0 w-0 h-px bg-white/60 group-hover:w-full transition-all duration-300" />
          </a>
          <button
            data-testid="nav-book-now-btn"
            onClick={onBookNow}
            className="btn-alchemist border border-white/20 bg-white/5 hover:bg-white hover:text-black py-2.5 px-6 uppercase tracking-widest text-[10px] font-sub transition-colors duration-300"
          >
            Book Now
          </button>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden text-white/60 hover:text-white transition-colors duration-300"
        >
          {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden glass mt-2 mx-4 p-6 animate-slide-down"
        >
          <div className="flex flex-col gap-6">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="font-sub text-xs text-white/50 hover:text-white tracking-widest transition-colors duration-300"
              >
                {link.label.toUpperCase()}
              </a>
            ))}
            <button
              onClick={() => { setMenuOpen(false); onBookNow(); }}
              className="btn-alchemist border border-white/20 bg-white/5 hover:bg-white hover:text-black py-3 px-6 uppercase tracking-widest text-xs font-sub transition-colors duration-300 text-center"
            >
              Book Now
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};
